import { Button, Modal } from 'components/core';
import { buildSubscriptionFrequenciesByQuantity, Styled } from './';
import { Product } from 'types';
import { SetStateAction, useState } from 'react';
import { FaRegCheckCircle } from 'react-icons/fa';
import { useIsMutating } from 'react-query';
import { ServerStateKeysEnum } from 'reactQuery/constants';
import { useI18n } from 'contexts/i18n';
import { ClientSubscriptionProduct } from 'types/Subscription.types';

const SubscriptionDiscountInfoModal = ({
  product,
  subscriptionProduct,
  onClose,
  setSelectedFrequencyId,
  selectedFrequencyId,
  setAvailableSubscriptionFrequencies,
  setSubscriptionQuantity,
  subscriptionQuantity,
  handleAddToCart,
}: {
  product: Product;
  subscriptionProduct: ClientSubscriptionProduct;
  onClose: () => void;
  selectedFrequencyId: string;
  setSelectedFrequencyId: (value: SetStateAction<string>) => void;
  setAvailableSubscriptionFrequencies: (
    value: SetStateAction<{ label: string; value: string }[]>
  ) => void;
  subscriptionQuantity: string | undefined;
  setSubscriptionQuantity: (value: SetStateAction<string | undefined>) => void;
  handleAddToCart: (
    isSubscriptionChecked?: boolean,
    subscriptionFrequencyId?: string
  ) => void;
}) => {
  const i18n = useI18n();
  const [activeQuantityIndex, setActiveQuantityIndex] = useState(() => {
    if (subscriptionQuantity) {
      const idx = subscriptionProduct.options.findIndex(
        opt => opt.quantity.toString() === subscriptionQuantity
      );
      return idx >= 0 ? idx : 0;
    }
    return 0;
  });

  const activeOption =
    subscriptionProduct.options[activeQuantityIndex] ??
    ({ quantity: 0, frequencies: [] } as ClientSubscriptionProduct['options'][number]);
  const onOptionSelect = (
    frequencyId: string,
    qty: string | undefined
  ) => {
    if (qty) {
      setSubscriptionQuantity(qty);
      const availableFrequencies = buildSubscriptionFrequenciesByQuantity(
        subscriptionProduct,
        parseInt(qty)
      );
      setAvailableSubscriptionFrequencies(availableFrequencies);
      const selectedFreq = availableFrequencies.find(
        freq => freq.value === frequencyId
      );
      if (selectedFreq) {
        setSelectedFrequencyId(selectedFreq.value);
      }
    }
  };

  const handleQuantityChange = (index: number) => {
    setActiveQuantityIndex(index);
    const option = subscriptionProduct.options[index];
    const firstFrequency = option.frequencies[0];
    if (firstFrequency) {
      onOptionSelect(firstFrequency.id, option.quantity.toString());
    }
  };

  const isAddLineItemMutating = useIsMutating(
    ServerStateKeysEnum.addLineitemMutation
  );

  return (
    <Modal overlaysize="small" width="480px">
      <Styled.SubPlanContainer>
        <Styled.SubPlanHeader>
          {i18n['Select Your Subscription Plan']}
        </Styled.SubPlanHeader>

        <Styled.SubPlanSection>
          <Styled.SubPlanSectionLabel>
            {i18n['Quantity']}
          </Styled.SubPlanSectionLabel>
          <Styled.SubPlanQuantityRow>
            {subscriptionProduct.options.map((option, index) => (
              <Styled.SubPlanQuantityPill
                key={option.quantity}
                isActive={index === activeQuantityIndex}
                onClick={() => handleQuantityChange(index)}
              >
                {option.quantity}
              </Styled.SubPlanQuantityPill>
            ))}
          </Styled.SubPlanQuantityRow>
        </Styled.SubPlanSection>

        <Styled.SubPlanSection>
          <Styled.SubPlanSectionLabel>
            {i18n['Delivery Frequency']}
          </Styled.SubPlanSectionLabel>
          <Styled.SubPlanFrequencyList>
            {activeOption.frequencies.map(frequency => {
              const isSelected =
                selectedFrequencyId === frequency.id &&
                subscriptionQuantity === activeOption.quantity.toString();
              return (
                <Styled.SubPlanFrequencyCard
                  key={frequency.id}
                  isSelected={isSelected}
                  onClick={() =>
                    onOptionSelect(
                      frequency.id,
                      activeOption.quantity.toString()
                    )
                  }
                >
                  <Styled.SubPlanFreqCardLeft>
                    <Styled.SubPlanFreqRadio isSelected={isSelected}>
                      {isSelected && <FaRegCheckCircle size="16" />}
                    </Styled.SubPlanFreqRadio>
                    <Styled.SubPlanFreqLabel>
                      {i18n['Every']}{' '}
                      {i18n[frequency.displayText] || frequency.displayText}
                    </Styled.SubPlanFreqLabel>
                  </Styled.SubPlanFreqCardLeft>
                  <Styled.SubPlanFreqCardRight>
                    <Styled.SubPlanFreqDiscount>
                      {frequency.discount} {i18n['off'] ?? 'off'}
                    </Styled.SubPlanFreqDiscount>
                    <Styled.SubPlanFreqPricing>
                      <Styled.SubPlanOriginalPrice>
                        {frequency.originalPrice}
                      </Styled.SubPlanOriginalPrice>
                      <Styled.SubPlanDiscountedPrice>
                        {frequency.discountedPrice}
                      </Styled.SubPlanDiscountedPrice>
                    </Styled.SubPlanFreqPricing>
                  </Styled.SubPlanFreqCardRight>
                </Styled.SubPlanFrequencyCard>
              );
            })}
          </Styled.SubPlanFrequencyList>
        </Styled.SubPlanSection>

        <Styled.SubPlanFooter>
          <Button onClick={onClose} variant="secondary">
            {i18n['Close']}
          </Button>
          <Button
            onClick={() => {
              handleAddToCart(true, selectedFrequencyId);
              onClose();
            }}
            variant="primary"
            loading={isAddLineItemMutating > 0}
            disabled={!product.inStock}
          >
            {product.inStock ? i18n['Add to cart'] : i18n['Out of Stock']}
          </Button>
        </Styled.SubPlanFooter>
      </Styled.SubPlanContainer>
    </Modal>
  );
};

export default SubscriptionDiscountInfoModal;
